import React, { useEffect, useState } from 'react';
import api from './api';
import { ReceivingRows } from './ReceivingHistoryLog';
import { VerificationRows, markedByHand } from './VerificationLog';

// Parses lines written by the backend's LogAppErrorAsync:
// "2026-09-28T18:20:00.0000000Z | type=submission | org=utech | order=17654 | msg=..."
const parseLine = (line) => {
  const parts = line.split(' | ');
  const get = (prefix) => {
    const match = parts.find(p => p.startsWith(prefix));
    return match ? match.slice(prefix.length) : '';
  };
  return {
    raw: line,
    timestamp: parts[0] || '',
    type: get('type='),
    org: get('org='),
    order: get('order='),
    message: get('msg='),
  };
};

// One-line version of an error for the collapsed row. Striven repeats the same
// sentence once per line item (hundreds of times on big orders), so identical
// sentences are folded into one with a count.
const summarize = (entry) => {
  if (entry.type === 'network') {
    const code = entry.message.match(/code=(\S+)/)?.[1];
    const size = entry.message.match(/size=(\S+)/)?.[1];
    const text = entry.message.match(/msg=(.*?)(?: size=\S+)?$/)?.[1] || 'Network Error';
    return [[text, code, size && `upload was ${size}`].filter(Boolean).join(' · ')];
  }
  if (entry.type !== 'submission') return [entry.message];
  const inner = entry.message.match(/"message"\s*:\s*"((?:[^"\\]|\\.)*)"/)?.[1] || entry.message;
  const counts = new Map();
  inner.split(/,\s+(?=[A-Z])/).forEach(part => {
    const key = part.trim();
    if (key) counts.set(key, (counts.get(key) || 0) + 1);
  });
  return [...counts].map(([text, n]) => (n > 1 ? `${text} (×${n})` : text));
};

const TYPE_STYLES = {
  network: { bg: '#eef2ff', color: '#4338ca', label: 'Network' },
  submission: { bg: '#fef2f2', color: '#b91c1c', label: 'Submission' },
  email: { bg: '#fffbeb', color: '#b45309', label: 'Email' },
  attachment: { bg: '#fffbeb', color: '#b45309', label: 'Attachment' },
  receiving: { bg: '#f0fdf4', color: '#15803d', label: 'PO Receiving' },
};

export default function ErrorLogPage({ user, msalInstance }) {
  const [section, setSection] = useState('errors');
  const [entries, setEntries] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [receiptFilter, setReceiptFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [syncFilter, setSyncFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [personFilter, setPersonFilter] = useState('all');
  const [expanded, setExpanded] = useState(() => new Set());

  const toggle = (key) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const getAuthHeaders = async () => {
    const authProvider = localStorage.getItem('authProvider') || 'microsoft';
    if (authProvider === 'google') {
      const token = localStorage.getItem('authToken');
      return { Authorization: `Bearer ${token}` };
    }
    const configRes = await api.get('/api/config/azure');
    const { clientId } = configRes.data;
    const accounts = msalInstance.getAllAccounts();
    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [`api://${clientId}/access_as_user`],
      account: accounts[0],
    });
    return { Authorization: `Bearer ${tokenResponse.accessToken}` };
  };

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const headers = await getAuthHeaders();
      const [errRes, verRes, recRes] = await Promise.all([
        api.get('/api/admin/error-log', { headers }),
        api.get('/api/admin/verification-log', { headers }),
        // A backend without the receiving history yet shouldn't take the whole page down.
        api.get('/api/admin/receiving-history', { headers }).catch(() => ({ data: [] })),
      ]);
      // Rows now come from the shared app_error_log table; plain strings are
      // the older per-server log file format.
      setEntries((errRes.data || []).map(r => (typeof r === 'string' ? parseLine(r) : {
        raw: `db-${r.id}`,
        timestamp: r.timestamp,
        type: r.type,
        org: r.org || '',
        order: r.order || '',
        message: r.message || '',
      })));
      setVerifications(verRes.data || []);
      setReceipts(recRes.data || []);
    } catch (err) {
      console.error('Failed to load logs:', err);
      setError('Could not load the logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  const orgs = [...new Set([...entries.map(e => e.org), ...verifications.map(v => v.orgSlug), ...receipts.map(r => r.org)].filter(Boolean))].sort();
  const inOrg = (org) => orgFilter === 'all' || org === orgFilter;
  const errorsInOrg = entries.filter(e => inOrg(e.org));
  const verificationsInOrg = verifications.filter(v => inOrg(v.orgSlug));
  const visibleErrors = filter === 'all' ? errorsInOrg : errorsInOrg.filter(e => e.type === filter);
  // People are only listed once a single org is picked, since names mean little across orgs.
  const people = orgFilter === 'all'
    ? []
    : [...new Set(verificationsInOrg.map(v => v.verifiedBy).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const verificationsForPerson = personFilter === 'all' || orgFilter === 'all'
    ? verificationsInOrg
    : verificationsInOrg.filter(v => v.verifiedBy === personFilter);
  const visibleVerifications = syncFilter === 'all'
    ? verificationsForPerson
    : verificationsForPerson.filter(v => (syncFilter === 'synced') === (v.strivenSynced || markedByHand(v)));
  const notSyncedCount = verificationsInOrg.filter(v => !v.strivenSynced && !markedByHand(v)).length;

  const receiptsInOrg = receipts.filter(r => inOrg(r.org));
  const receiptPeople = orgFilter === 'all'
    ? []
    : [...new Set(receiptsInOrg.map(r => r.by).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  const receiptsForPerson = personFilter === 'all' || orgFilter === 'all'
    ? receiptsInOrg
    : receiptsInOrg.filter(r => r.by === personFilter);
  const visibleReceipts = receiptFilter === 'all'
    ? receiptsForPerson
    : receiptsForPerson.filter(r => (receiptFilter === 'received') === (r.status === 'received' || r.done));
  const receiptsNotInStriven = receiptsInOrg.filter(r => r.status !== 'received' && !r.done).length;

  return (
    <div style={s.page}>
      <div style={s.titleRow}>
        <h1 style={s.title}>Logs</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {orgs.length > 1 && (
            <select value={orgFilter} onChange={e => { setOrgFilter(e.target.value); setPersonFilter('all'); }} style={s.select} aria-label="Organization">
              <option value="all">All orgs</option>
              {orgs.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          )}
          {section === 'errors' ? (
            <select value={filter} onChange={e => setFilter(e.target.value)} style={s.select}>
              <option value="all">All errors</option>
              <option value="submission">Submission errors</option>
              <option value="network">Network errors</option>
              <option value="email">Email errors</option>
              <option value="attachment">Attachment errors</option>
              <option value="receiving">PO Receiving errors</option>
            </select>
          ) : section === 'receiving' ? (
            <>
            {receiptPeople.length > 0 && (
              <select value={personFilter} onChange={e => setPersonFilter(e.target.value)} style={s.select} aria-label="Person">
                <option value="all">All people</option>
                {receiptPeople.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            <select value={receiptFilter} onChange={e => setReceiptFilter(e.target.value)} style={s.select}>
              <option value="all">All receipts</option>
              <option value="not-received">Not in Striven</option>
              <option value="received">Received in Striven</option>
            </select>
            </>
          ) : (
            <>
            {people.length > 0 && (
              <select value={personFilter} onChange={e => setPersonFilter(e.target.value)} style={s.select} aria-label="Person">
                <option value="all">All people</option>
                {people.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
            <select value={syncFilter} onChange={e => setSyncFilter(e.target.value)} style={s.select}>
              <option value="all">All verifications</option>
              <option value="not-synced">Not updated in Striven</option>
              <option value="synced">Updated in Striven</option>
            </select>
            </>
          )}
          <button style={s.refreshBtn} onClick={fetchLogs} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div style={s.tabs} role="tablist">
        <button
          role="tab"
          aria-selected={section === 'errors'}
          style={{ ...s.tab, ...(section === 'errors' ? s.tabActive : {}) }}
          onClick={() => setSection('errors')}
        >
          Errors ({errorsInOrg.length})
        </button>
        <button
          role="tab"
          aria-selected={section === 'verifications'}
          style={{ ...s.tab, ...(section === 'verifications' ? s.tabActive : {}) }}
          onClick={() => setSection('verifications')}
        >
          Verifications ({verificationsInOrg.length}{notSyncedCount ? `, ${notSyncedCount} not in Striven` : ''})
        </button>
        <button
          role="tab"
          aria-selected={section === 'receiving'}
          style={{ ...s.tab, ...(section === 'receiving' ? s.tabActive : {}) }}
          onClick={() => { setSection('receiving'); setPersonFilter('all'); }}
        >
          PO Receiving ({receiptsInOrg.length}{receiptsNotInStriven ? `, ${receiptsNotInStriven} not in Striven` : ''})
        </button>
      </div>

      <p style={s.sub}>
        {section === 'errors'
          ? 'Every Striven rejection and every network error hit while saving, logged as it happens.'
          : section === 'receiving'
          ? 'Every time Update PO was pressed in PO Receiving, and whether Striven took it. "Not in Striven" means Striven refused it (a vendor that is not in CAD, or a PO that is not approved), so it needs receiving by hand.'
          : 'Every order verified in 2Check, and whether Striven was actually updated. "Not in Striven" means Striven rejected it (or sync was off), so it still needs marking in Striven by hand. "Marked by hand *" means it was marked in Striven by a person, not by 2Check.'}
      </p>

      {error && <p style={s.error}>{error}</p>}

      {section === 'errors' && (
        <>
          {!error && !loading && visibleErrors.length === 0 && <p style={s.empty}>No errors logged yet.</p>}
          <div style={s.list}>
            {visibleErrors.map((e, i) => {
              const style = TYPE_STYLES[e.type] || { bg: '#f3f4f6', color: '#374151', label: e.type || 'unknown' };
              let localTime = e.timestamp;
              try { localTime = new Date(e.timestamp).toLocaleString(); } catch {}
              const open = expanded.has(e.raw);
              const summary = summarize(e);
              return (
                <div key={i} style={s.row}>
                  <button type="button" style={s.rowBtn} aria-expanded={open} onClick={() => toggle(e.raw)}>
                    <div style={s.rowHead}>
                      <span style={{ ...s.chevron, transform: open ? 'rotate(90deg)' : 'none' }}>▸</span>
                      <span style={{ ...s.badge, background: style.bg, color: style.color }}>{style.label}</span>
                      {e.order && <span style={s.orderTag}>Order #{e.order}</span>}
                      {e.org && <span style={s.orgTag}>{e.org}</span>}
                      <span style={s.time}>{localTime}</span>
                    </div>
                    <div style={open ? s.summary : { ...s.summary, ...s.clamp }}>
                      {(open ? summary : summary.slice(0, 2)).map((line, j) => <div key={j}>{line}</div>)}
                      {!open && summary.length > 2 && <div style={s.more}>+ {summary.length - 2} more</div>}
                    </div>
                  </button>
                  {open && (
                    <>
                      <div style={s.detailLabel}>Full details</div>
                      <div style={s.message}>{e.message}</div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {section === 'receiving' && (
        <>
          {!error && !loading && visibleReceipts.length === 0 && <p style={s.empty}>No receipts to show.</p>}
          <ReceivingRows receipts={visibleReceipts} showOrg />
        </>
      )}

      {section === 'verifications' && (
        <>
          {!error && !loading && visibleVerifications.length === 0 && <p style={s.empty}>No verifications to show.</p>}
          <VerificationRows
            verifications={visibleVerifications}
            errors={entries}
            summarizeError={(e) => summarize(e)[0]}
            showOrg
          />
        </>
      )}
    </div>
  );
}

const s = {
  page: { padding: '32px 24px', maxWidth: 900, margin: '0 auto' },
  titleRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  sub: { fontSize: 13, color: '#888', marginBottom: 24, marginTop: 0 },
  select: { padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13 },
  refreshBtn: { padding: '6px 16px', background: 'rgb(65, 159, 196)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  error: { color: '#b91c1c', fontSize: 14 },
  empty: { color: '#aaa', fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  row: { background: '#fff', border: '1px solid #eee', borderRadius: 10, padding: '14px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  rowHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' },
  badge: { fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, textTransform: 'uppercase', letterSpacing: 0.3 },
  orderTag: { fontSize: 13, fontWeight: 600, color: '#333' },
  time: { fontSize: 12, color: '#999', marginLeft: 'auto' },
  orgTag: { fontSize: 12, color: '#666', background: '#f3f4f6', padding: '2px 8px', borderRadius: 100 },
  orderName: { fontSize: 13, color: '#555' },
  tabs: { display: 'flex', gap: 6, marginBottom: 12, borderBottom: '1px solid #eee', flexWrap: 'wrap' },
  tab: { padding: '8px 14px', background: 'none', border: 'none', borderBottom: '2px solid transparent', cursor: 'pointer', fontSize: 14, color: '#666', marginBottom: -1 },
  tabActive: { color: '#111', fontWeight: 600, borderBottom: '2px solid rgb(65, 159, 196)' },
  message: { fontSize: 12, color: '#444', fontFamily: 'ui-monospace, monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320, overflowY: 'auto', background: '#fafafa', border: '1px solid #eee', borderRadius: 6, padding: 10 },
  rowBtn: { display: 'block', width: '100%', padding: 0, background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' },
  chevron: { fontSize: 12, color: '#999', transition: 'transform 0.15s', display: 'inline-block' },
  summary: { fontSize: 13, color: '#444', wordBreak: 'break-word', paddingLeft: 22 },
  clamp: { maxHeight: 60, overflow: 'hidden' },
  more: { color: '#999', fontSize: 12 },
  detailLabel: { fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.3, margin: '12px 0 6px' },
  details: { marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, color: '#444' },
  detailRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  detailKey: { width: 150, flexShrink: 0, color: '#888' },
  failed: { color: '#b91c1c', fontWeight: 600 },
  note: { fontSize: 12, color: '#999' },
  tableWrap: { overflowX: 'auto', marginTop: 6 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' },
  th: { textAlign: 'right', padding: '6px 10px', borderBottom: '1px solid #e5e7eb', color: '#888', fontWeight: 600, fontSize: 12 },
  td: { textAlign: 'right', padding: '6px 10px', borderBottom: '1px solid #f3f4f6' },
  errorTag: { fontSize: 12, color: '#b91c1c', background: '#fef2f2', padding: '2px 8px', borderRadius: 100 },
};
